import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Box,
  Button,
  TableContainer,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import api from "../../../services/api";
import DisclaimerFormModal from "./DisclaimerFormModal";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";

const DisclaimerTable = () => {
  const [disclaimers, setDisclaimers] = useState([]);
  const [selectedDisclaimer, setSelectedDisclaimer] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const snackbar = useSnackbarAlert();

  const fetchDisclaimers = async () => {
    try {
      const res = await api.get("/admin/disclaimers");
      setDisclaimers(res.data);
    } catch (err) {
      snackbar.error("Failed to load disclaimers");
    }
  };

  useEffect(() => {
    fetchDisclaimers();
  }, []);

  const handleEdit = (item) => {
    setSelectedDisclaimer(item);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this disclaimer?"))
      return;
    try {
      await api.delete(`/admin/disclaimers/${id}`);
      snackbar.success("Disclaimer deleted");
      fetchDisclaimers();
    } catch (err) {
      snackbar.error("Failed to delete disclaimer");
    }
  };

  const handleSave = () => {
    setModalOpen(false);
    setSelectedDisclaimer(null);
    fetchDisclaimers(); // ✅ only refetch, not re-submit
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}>
        <Typography variant="h6">Disclaimers</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedDisclaimer(null);
            setModalOpen(true);
          }}>
          Add Disclaimer
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Display Name</TableCell>
              <TableCell>Internal Name</TableCell>
              <TableCell>Text</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {disclaimers.map((disclaimer) => (
              <TableRow key={disclaimer._id}>
                <TableCell>{disclaimer.displayName}</TableCell>
                <TableCell>{disclaimer.name}</TableCell>
                <TableCell sx={{ maxWidth: "400px" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    {disclaimer.text}
                  </Typography>
                </TableCell>

                <TableCell align="right">
                  <IconButton onClick={() => handleEdit(disclaimer)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(disclaimer._id)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {disclaimers.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center">
                    No disclaimers found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DisclaimerFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedDisclaimer(null);
        }}
        onSave={handleSave}
        disclaimer={selectedDisclaimer}
      />
    </Box>
  );
};

export default DisclaimerTable;
